import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { ArrowLeft, BookOpen, Printer, Target } from 'lucide-react';
import { toast } from 'sonner';
import AIPracticeScenario from '../shared/AIPracticeScenario';
import PageMessaging from '../shared/PageMessaging';

export default function ThreeAreasAttention() {
  const navigate = useNavigate();
  const printPage = () => { window.print(); toast.success('Print dialog opened'); };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <style>{`@media print { body { background: white !important; } .no-print { display: none !important; } .print-page { background: white !important; color: black !important; } }`}</style>
      
      <div className="border-b border-slate-700/50 bg-slate-900/90 backdrop-blur-sm sticky top-0 z-50 no-print">
        <div className="container mx-auto px-6 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button onClick={() => navigate('/belonging-ebook')} variant="outline" size="sm" className="border-slate-600 hover:border-slate-500 text-white hover:bg-slate-800">
                <ArrowLeft className="h-4 w-4 mr-2" />Back to Guide
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-white">The Three Areas of Attention</h1>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button onClick={printPage} variant="outline" size="sm" className="border-blue-600 hover:bg-blue-900/20 text-white">
                <Printer className="h-4 w-4 mr-2" />Print
              </Button>
              <div className="flex items-center gap-2 text-slate-400 text-sm">
                <BookOpen className="h-4 w-4" />
                <span>Page 48</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-6 py-12 print-page">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-8">
            <Target className="h-16 w-16 text-purple-400 mx-auto mb-4" />
            <h2 className="text-3xl font-bold text-white mb-4">The Three Areas of Attention</h2>
            <p className="text-slate-300 leading-relaxed">
              Understanding how our minds work to develop more effective beliefs and behaviors
            </p>
          </div>

          <div className="bg-blue-900/20 border border-blue-700/30 rounded-lg p-6 mb-8">
            <p className="text-blue-100 leading-relaxed mb-3">
              To develop more effective beliefs and behaviors that will create more satisfying relationships, it is important to understand how our minds work. This model is one of the most fundamental concepts for making positive changes.
            </p>
            <p className="text-blue-200 font-semibold">
              These three areas comprise the totality of what we can experience. We are only capable of focusing on one area at a time.
            </p>
          </div>

          {/* The Diagram */}
          <div className="bg-white rounded-lg p-6 mb-8">
            <img 
              src="https://customer-assets.emergentagent.com/job_belonging-sessions-1/artifacts/t0ghy47k_Screenshot%202025-11-15%20at%203.21.19%E2%80%AFAM.png"
              alt="The 3 Areas of Attention: Body Sensations, Things Outside of Us, Thoughts"
              className="w-full h-auto rounded"
              style={{ imageRendering: 'high-quality', maxWidth: '100%' }}
            />
          </div>

          {/* Three Areas Explained */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="bg-gradient-to-br from-orange-900/20 to-amber-900/20 border border-orange-700/30 rounded-lg p-6">
              <h3 className="text-xl font-bold text-orange-200 mb-3">Body Sensations</h3>
              <p className="text-slate-200 leading-relaxed text-sm">
                Physical experiences and sensations in our body
              </p>
            </div>
            <div className="bg-gradient-to-br from-red-900/20 to-rose-900/20 border border-red-700/30 rounded-lg p-6">
              <h3 className="text-xl font-bold text-red-200 mb-3">Things Outside of Us</h3>
              <p className="text-slate-200 leading-relaxed text-sm">
                External events, people, and circumstances
              </p>
            </div>
            <div className="bg-gradient-to-br from-purple-900/20 to-pink-900/20 border border-purple-700/30 rounded-lg p-6">
              <h3 className="text-xl font-bold text-purple-200 mb-3">Thoughts</h3>
              <p className="text-slate-200 leading-relaxed text-sm">
                Our interpretations, beliefs, and mental narratives
              </p>
            </div>
          </div>

          {/* Key Concepts */}
          <div className="space-y-6 mb-8">
            <div className="bg-slate-800/40 border border-slate-700/50 rounded-lg p-6">
              <h3 className="text-xl font-bold text-white mb-4">How It Works: Conditioning</h3>
              <p className="text-slate-200 leading-relaxed mb-4">
                What we experience is determined by the interrelationship of these three areas. For example, things can happen close to us that create physical sensations to which we then connect a thought.
              </p>
              <div className="bg-amber-900/20 border border-amber-700/30 rounded p-4">
                <p className="text-amber-100 mb-2"><span className="font-semibold">Example:</span> Fire touches my skin.</p>
                <p className="text-slate-300 text-sm mb-1">→ I experience pain</p>
                <p className="text-slate-300 text-sm mb-1">→ The thought emerges that "fire is dangerous"</p>
                <p className="text-slate-300 text-sm italic mt-3">Or I might experience a pleasant rush of energy and think, "fire is exciting!"</p>
              </div>
            </div>

            <div className="bg-gradient-to-br from-green-900/20 to-emerald-900/20 border border-green-700/30 rounded-lg p-6">
              <h3 className="text-xl font-bold text-white mb-4">The Power of Awareness</h3>
              <p className="text-green-100 leading-relaxed mb-3">
                This process is called "conditioning." The thoughts we create become filters that condition or "color" our subsequent experiences. Just thinking of fire can create a bodily sensation, just as you can make your arms tingle just by thinking about it.
              </p>
              <p className="text-green-200 font-semibold">
                The point is that what we think can have a profound effect on what we experience. So can the act of taking your attention OFF of thinking. You don't have to listen to everything you think!
              </p>
            </div>
          </div>

          <div className="text-center text-slate-500 text-xs mb-8">
            ©Steve Torma • The REAL Center • www.therealcenter.org • 828-254-5613
          </div>

          <AIPracticeScenario pageTitle="The Three Areas of Attention" pageNumber={48} />
          <PageMessaging pageTitle="The Three Areas of Attention" pageNumber={48} />
        </div>
      </div>
    </div>
  );
}
